import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useMemo, useRef, useState } from "react";
import { Dimension, Metric, SyntaxchecksData, Timing } from "./AppTypes";
import { notifyProjectActivity } from "./api/pingClient";
import { getBaseColor, Mode } from "./colorUtils";
import { makeId } from "./xmlHandling";

const SPECIAL_CHARACTERS = ["·", ";", "\n", "|"];
const stripSpecialChars = (v: string) =>
  SPECIAL_CHARACTERS.reduce((s, c) => s.split(c).join(""), v);

type MetricRole = Metric["dimrefs"][number]["role"];
type MetricDimref = Metric["dimrefs"][number];
type MetricSelectorMode = MetricDimref["mode"];
type DragItemType = "dimension" | "point" | "tag";

type Props = {
  metrics: Metric[];
  onChange: (metrics: Metric[]) => void;
  dimensions: Dimension[];
  timings: Timing[];
  allNames: string[];
  isActive: boolean;
  toggle: () => void;
  userHue: number;
  mode: Mode;
  projectId: number;
  syntaxData?: SyntaxchecksData | null;
};

function rolesForType(type: Metric["type"]): MetricRole[] {
  return ["for", "where"];
}

function hasTransferType(dataTransfer: DataTransfer, type: string): boolean {
  return Array.from(dataTransfer.types as any).indexOf(type) !== -1;
}

function selectorLabel(dim: Dimension, dr: MetricDimref, type: DragItemType, id?: string): string {
  if (type === "dimension") return dim.name;
  if (type === "tag") return dim.tags.find((t) => t.id === id)?.name ?? "";
  return dim.points.find((p) => p.id === id)?.name ?? "";
}

function DropZone({
  role,
  metric,
  dimensions,
  onAddDimref,
  onUpdateDimref,
  onRemoveDimref,
  onMoveDimref,
  droppedInZoneRef,
}: {
  role: MetricRole;
  metric: Metric;
  dimensions: Dimension[];
  onAddDimref: (
    dim: Dimension,
    role: MetricRole,
    opts: { mode: MetricSelectorMode; pointIds: string[]; tagIds: string[] }
  ) => void;
  onUpdateDimref: (dimrefId: string, updates: Partial<MetricDimref>) => void;
  onRemoveDimref: (dimrefId: string) => void;
  onMoveDimref: (srcDimrefId: string, targetRole: MetricRole, type: DragItemType, itemId?: string) => void;
  droppedInZoneRef: React.MutableRefObject<boolean>;
}) {
  const [isOver, setIsOver] = useState(false);
  const roleDimrefs = metric.dimrefs.filter((dr) => dr.role === role);

  const acceptsDrop = (dataTransfer: DataTransfer) =>
    hasTransferType(dataTransfer, "application/x-zeitgeist") ||
    hasTransferType(dataTransfer, "application/x-metric-dimref");

  const addExternalPayload = (payload: any) => {
    const dim = dimensions.find((d) => d.id === payload.dimensionId);
    if (!dim) return;

    const existing = metric.dimrefs.find(
      (dr) => dr.dimensionId === payload.dimensionId && dr.role === role
    );

    if (payload.kind === "create-condition-from-dimension") {
      if (existing) onUpdateDimref(existing.id, { mode: "all", tagIds: [], pointIds: [] });
      else onAddDimref(dim, role, { mode: "all", pointIds: [], tagIds: [] });
      return;
    }

    if (payload.kind === "create-condition-from-dimension-point") {
      if (existing) {
        const nextPointIds =
          existing.mode === "points" && existing.pointIds.indexOf(payload.pointId) === -1
            ? [...existing.pointIds, payload.pointId]
            : existing.mode === "points"
              ? existing.pointIds
              : [payload.pointId];
        onUpdateDimref(existing.id, { mode: "points", pointIds: nextPointIds, tagIds: [] });
      } else {
        onAddDimref(dim, role, { mode: "points", pointIds: [payload.pointId], tagIds: [] });
      }
      return;
    }

    if (payload.kind === "create-condition-from-dimension-tag") {
      if (existing) {
        const nextTagIds =
          existing.mode === "tags" && existing.tagIds.indexOf(payload.tagId) === -1
            ? [...existing.tagIds, payload.tagId]
            : existing.mode === "tags"
              ? existing.tagIds
              : [payload.tagId];
        onUpdateDimref(existing.id, { mode: "tags", tagIds: nextTagIds, pointIds: [] });
      } else {
        onAddDimref(dim, role, { mode: "tags", pointIds: [], tagIds: [payload.tagId] });
      }
    }
  };

  const renderChip = (dr: MetricDimref, type: DragItemType, itemId?: string) => {
    const dim = dimensions.find((d) => d.id === dr.dimensionId);
    if (!dim) return null;
    const label = selectorLabel(dim, dr, type, itemId);
    if (!label) return null;

    return (
      <div
        key={`${dr.id}-${type}-${itemId || dr.dimensionId}`}
        className="metric-chip"
        draggable
        onDragStart={(e) => {
          droppedInZoneRef.current = false;
          const payload = {
            dimrefId: dr.id,
            metricId: metric.id,
            type,
            pointId: type === "point" ? itemId : undefined,
            tagId: type === "tag" ? itemId : undefined,
          };
          e.dataTransfer.setData("application/x-metric-dimref", JSON.stringify(payload));
          e.dataTransfer.effectAllowed = "move";
          (e.currentTarget as HTMLElement).classList.add("metric-chip--dragging");
        }}
        onDragEnd={(e) => {
          (e.currentTarget as HTMLElement).classList.remove("metric-chip--dragging");
          if (droppedInZoneRef.current) return;

          if (type === "dimension") {
            onRemoveDimref(dr.id);
          } else if (type === "point" && itemId) {
            const remaining = dr.pointIds.filter((id) => id !== itemId);
            if (remaining.length > 0) onUpdateDimref(dr.id, { pointIds: remaining });
            else onRemoveDimref(dr.id);
          } else if (type === "tag" && itemId) {
            const remaining = dr.tagIds.filter((id) => id !== itemId);
            if (remaining.length > 0) onUpdateDimref(dr.id, { tagIds: remaining });
            else onRemoveDimref(dr.id);
          }
        }}
        title="Drag out to remove"
      >
        {label}
      </div>
    );
  };

  return (
    <div
      className={`metric-drop-zone ${isOver ? "metric-drop-zone--over" : ""}`}
      onDragOver={(e) => {
        if (!acceptsDrop(e.dataTransfer)) return;
        e.preventDefault();
        setIsOver(true);
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={(e) => {
        if (!acceptsDrop(e.dataTransfer)) return;
        e.preventDefault();
        setIsOver(false);

        const internalRaw = e.dataTransfer.getData("application/x-metric-dimref");
        if (internalRaw) {
          droppedInZoneRef.current = true;
          try {
            const payload = JSON.parse(internalRaw);
            const source = metric.dimrefs.find((dr) => dr.id === payload.dimrefId);
            if (source && source.role !== role) {
              onMoveDimref(
                source.id,
                role,
                payload.type,
                payload.type === "point" ? payload.pointId : payload.tagId
              );
            }
          } catch {
            /* ignore invalid drag payloads */
          }
          return;
        }

        const raw = e.dataTransfer.getData("application/x-zeitgeist");
        if (!raw) return;
        try {
          addExternalPayload(JSON.parse(raw));
        } catch {
          /* ignore invalid drag payloads */
        }
      }}
    >
      <span className="metric-drop-zone__label">{role}</span>
      <div className="metric-drop-zone__chips">
        {roleDimrefs.map((dr) => {
          if (dr.mode === "points" && dr.pointIds.length > 0) {
            return dr.pointIds.map((pointId) => renderChip(dr, "point", pointId));
          }
          if (dr.mode === "tags" && dr.tagIds.length > 0) {
            return dr.tagIds.map((tagId) => renderChip(dr, "tag", tagId));
          }
          return renderChip(dr, "dimension");
        })}
      </div>
    </div>
  );
}

function MetricCard({
  metric,
  isOverlay,
  expandedId,
  setExpandedId,
  onUpdate,
  onDelete,
  dimensions,
  timings,
  allNames,
  userHue,
  mode,
  dragAttributes,
  dragListeners,
  dragRef,
  dragStyle,
  syntaxStat,
}: {
  metric: Metric;
  isOverlay?: boolean;
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
  onUpdate: (id: string, updates: Partial<Metric>) => void;
  onDelete: (id: string) => void;
  dimensions: Dimension[];
  timings: Timing[];
  allNames: string[];
  userHue: number;
  mode: Mode;
  dragAttributes?: any;
  dragListeners?: any;
  dragRef?: (node: HTMLElement | null) => void;
  dragStyle?: React.CSSProperties;
  syntaxStat?: { syntax?: string; SyntaxMessages?: string[] };
}) {
  const expanded = expandedId === metric.id && !isOverlay;
  const isDuplicate = allNames.filter((n) => n === metric.name).length > 1;
  const [isEditingName, setIsEditingName] = useState(false);
  const droppedInZoneRef = useRef(false);
  const timingNames = useMemo(() => {
    const names = (timings || []).map((timing) => timing.name).filter(Boolean);
    if (metric.time_ref && names.indexOf(metric.time_ref) === -1) {
      return [...names, metric.time_ref];
    }
    return names;
  }, [timings, metric.time_ref]);

  const backendErrors = syntaxStat?.SyntaxMessages;
  const errorMessage = backendErrors && backendErrors.length > 0
    ? backendErrors.map((e) => String(e)).join(", ")
    : null;
  const validationIcon = errorMessage && /warning/i.test(errorMessage) ? "❗️" : "⚠️";
  const cardTypeColor = "count";

  const addDimref = (
    dim: Dimension,
    role: MetricRole,
    opts: { mode: MetricSelectorMode; pointIds: string[]; tagIds: string[] }
  ) => {
    onUpdate(metric.id, {
      dimrefs: [
        ...metric.dimrefs,
        {
          id: makeId("dimref"),
          dimensionId: dim.id,
          role,
          mode: opts.mode,
          tagIds: opts.tagIds,
          pointIds: opts.pointIds,
        },
      ],
    });
  };

  const updateDimref = (dimrefId: string, updates: Partial<MetricDimref>) => {
    onUpdate(metric.id, {
      dimrefs: metric.dimrefs.map((dr) => dr.id === dimrefId ? { ...dr, ...updates } : dr),
    });
  };

  const removeDimref = (dimrefId: string) => {
    onUpdate(metric.id, {
      dimrefs: metric.dimrefs.filter((dr) => dr.id !== dimrefId),
    });
  };

  const moveDimref = (srcDimrefId: string, targetRole: MetricRole, type: DragItemType, itemId?: string) => {
    const src = metric.dimrefs.find((dr) => dr.id === srcDimrefId);
    if (!src) return;

    let next = metric.dimrefs.slice();

    if (type === "dimension") {
      next = next.map((dr) => dr.id === srcDimrefId ? { ...dr, role: targetRole } : dr);
      onUpdate(metric.id, { dimrefs: next });
      return;
    }

    const target = next.find((dr) => dr.dimensionId === src.dimensionId && dr.role === targetRole);

    if (type === "point" && itemId) {
      if (target) {
        const targetPointIds = target.mode === "points" && target.pointIds.indexOf(itemId) === -1
          ? [...target.pointIds, itemId]
          : target.mode === "points"
            ? target.pointIds
            : [itemId];
        next = next.map((dr) => dr.id === target.id ? { ...dr, mode: "points", pointIds: targetPointIds, tagIds: [] } : dr);
      } else {
        next.push({ id: makeId("dimref"), dimensionId: src.dimensionId, role: targetRole, mode: "points", pointIds: [itemId], tagIds: [] });
      }

      const remaining = src.pointIds.filter((id) => id !== itemId);
      next = remaining.length > 0
        ? next.map((dr) => dr.id === srcDimrefId ? { ...dr, pointIds: remaining } : dr)
        : next.filter((dr) => dr.id !== srcDimrefId);
    }

    if (type === "tag" && itemId) {
      if (target) {
        const targetTagIds = target.mode === "tags" && target.tagIds.indexOf(itemId) === -1
          ? [...target.tagIds, itemId]
          : target.mode === "tags"
            ? target.tagIds
            : [itemId];
        next = next.map((dr) => dr.id === target.id ? { ...dr, mode: "tags", tagIds: targetTagIds, pointIds: [] } : dr);
      } else {
        next.push({ id: makeId("dimref"), dimensionId: src.dimensionId, role: targetRole, mode: "tags", pointIds: [], tagIds: [itemId] });
      }

      const remaining = src.tagIds.filter((id) => id !== itemId);
      next = remaining.length > 0
        ? next.map((dr) => dr.id === srcDimrefId ? { ...dr, tagIds: remaining } : dr)
        : next.filter((dr) => dr.id !== srcDimrefId);
    }

    onUpdate(metric.id, { dimrefs: next });
  };

  return (
    <div
      ref={dragRef}
      className={`metric-card ${isOverlay ? "metric-card--overlay" : ""}`}
      style={{
        ...dragStyle,
        "--metric-card-bg": getBaseColor(3, cardTypeColor, userHue, mode),
      } as React.CSSProperties}
      {...dragAttributes}
    >
      <div className="metric-card__header">
        <div
          className="metric-card__drag"
          {...dragListeners}
          title="Drag to reorder"
        >
          ☰
        </div>
        <button
          className="metric-card__toggle"
          type="button"
          onClick={() => !isOverlay && setExpandedId(expanded ? null : metric.id)}
          aria-label={expanded ? "Collapse metric" : "Expand metric"}
        >
          {expanded ? "▾" : "▸"}
        </button>

        {isEditingName ? (
          <input
            className={`metric-card__name-input ${isDuplicate ? "metric-card__name-input--duplicate" : ""}`}
            value={metric.name}
            onChange={(e) => onUpdate(metric.id, { name: stripSpecialChars(e.target.value) })}
            onBlur={() => setIsEditingName(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter") setIsEditingName(false);
            }}
            autoFocus
          />
        ) : (
          <div
            className="metric-card__title"
            onDoubleClick={() => !isOverlay && setIsEditingName(true)}
          >
            <span>{metric.name}</span>
            {isDuplicate && <span className="metric-card__duplicate">Name already in use</span>}
          </div>
        )}

        {errorMessage && !isOverlay && (
          <span
            title={errorMessage}
            style={{ fontSize: "1.2em", cursor: "help", animation: "pulse 2s infinite" }}
          >
            {validationIcon}
          </span>
        )}
      </div>

      {expanded && !isOverlay && (
        <div className="metric-card__body" onPointerDown={(e) => e.stopPropagation()}>
          <div className="metric-card__controls">
            <label className="metric-field">
              <span>Type</span>
              <select
                value={metric.type}
                onChange={(e) => {
                  const nextType = e.target.value as Metric["type"];
                  onUpdate(metric.id, {
                    type: nextType,
                    dimrefs: metric.dimrefs.filter((dr) => rolesForType(nextType).indexOf(dr.role) !== -1),
                  });
                }}
              >
                <option value="count">count</option>
                <option value="time.sum">time.sum</option>
              </select>
            </label>

            <label className="metric-field metric-field--time">
              <span>Timing</span>
              <select
                value={metric.time_ref || ""}
                onChange={(e) => onUpdate(metric.id, { time_ref: e.target.value || undefined })}
              >
                <option value="">-</option>
                {timingNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="metric-card__zones">
            {rolesForType(metric.type).map((role) => (
              <DropZone
                key={role}
                role={role}
                metric={metric}
                dimensions={dimensions}
                onAddDimref={addDimref}
                onUpdateDimref={updateDimref}
                onRemoveDimref={removeDimref}
                onMoveDimref={moveDimref}
                droppedInZoneRef={droppedInZoneRef}
              />
            ))}
          </div>

          {errorMessage && <div className="syntax-error-banner">{errorMessage}</div>}
        </div>
      )}
    </div>
  );
}

function SortableMetricCard(
  props: Omit<
    React.ComponentProps<typeof MetricCard>,
    "dragAttributes" | "dragListeners" | "dragRef" | "dragStyle"
  >
) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: props.metric.id,
  });

  return (
    <MetricCard
      {...props}
      dragRef={setNodeRef}
      dragAttributes={attributes}
      dragListeners={listeners}
      dragStyle={{ transform: CSS.Transform.toString(transform), transition }}
    />
  );
}

export default function MetricEditor({
  metrics,
  onChange,
  dimensions,
  timings,
  allNames,
  isActive,
  toggle,
  userHue,
  mode,
  projectId,
  syntaxData,
}: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const editorFrameRef = useRef<HTMLDivElement | null>(null);

  const metricSyntaxMap = useMemo(() => {
    const map = new Map<string, { syntax?: string; SyntaxMessages: string[] }>();
    const arr = Array.isArray(syntaxData) ? syntaxData : [];
    arr.forEach((entry: any) => {
      const name = entry?.metricName;
      if (!name) return;
      map.set(name, {
        syntax: entry.syntax,
        SyntaxMessages: entry.message ? [entry.message] : [],
      });
    });
    return map;
  }, [syntaxData]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  const handleAdd = () => {
    const metric: Metric = {
      id: makeId("metric"),
      name: `Metric${metrics.length + 1}`,
      type: "count",
      time_ref: undefined,
      dimrefs: [],
    };
    onChange([...metrics, metric]);
    setExpandedId(metric.id);
    notifyProjectActivity(projectId).catch(() => {});
  };

  const handleUpdate = (id: string, updates: Partial<Metric>) => {
    onChange(metrics.map((metric) => metric.id === id ? { ...metric, ...updates } : metric));
    notifyProjectActivity(projectId).catch(() => {});
  };

  const handleDelete = (id: string) => {
    onChange(metrics.filter((metric) => metric.id !== id));
    if (expandedId === id) setExpandedId(null);
    notifyProjectActivity(projectId).catch(() => {});
  };

  const activeMetric = activeId ? metrics.find((metric) => metric.id === activeId) ?? null : null;

  return (
    <div
      ref={editorFrameRef}
      className="metric-editor"
      style={{
        "--metric-editor-bg": getBaseColor(2, "section", userHue, mode),
      } as React.CSSProperties}
    >
      <div className="metric-editor__header" onClick={toggle}>
        <strong className="metric-editor__title">{isActive ? "▾" : "▸"} Metrics</strong>
      </div>

      {isActive && (
        <div className="metric-editor__body">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e: DragStartEvent) => {
              setExpandedId(null);
              setActiveId(String(e.active.id));
            }}
            onDragEnd={(event: DragEndEvent) => {
              const { active, over } = event;
              setActiveId(null);

              const finalRect = active.rect.current.translated ?? active.rect.current.initial;
              const editorRect = editorFrameRef.current?.getBoundingClientRect();
              if (
                finalRect && editorRect &&
                (finalRect.right <= editorRect.left ||
                  finalRect.left >= editorRect.right ||
                  finalRect.bottom <= editorRect.top ||
                  finalRect.top >= editorRect.bottom)
              ) {
                handleDelete(String(active.id));
                return;
              }

              if (active.id !== over?.id) {
                const oldIndex = metrics.findIndex((metric) => metric.id === active.id);
                const newIndex = metrics.findIndex((metric) => metric.id === over?.id);
                if (oldIndex !== -1 && newIndex !== -1) {
                  onChange(arrayMove(metrics, oldIndex, newIndex));
                }
              }
            }}
          >
            <SortableContext items={metrics.map((metric) => metric.id)} strategy={verticalListSortingStrategy}>
              {metrics.map((metric) => (
                <SortableMetricCard
                  key={metric.id}
                  metric={metric}
                  expandedId={expandedId}
                  setExpandedId={setExpandedId}
                  onUpdate={handleUpdate}
                  onDelete={handleDelete}
                  dimensions={dimensions}
                  timings={timings}
                  allNames={allNames}
                  userHue={userHue}
                  mode={mode}
                  syntaxStat={metricSyntaxMap.get(metric.name)}
                />
              ))}
            </SortableContext>

            <DragOverlay>
              {activeMetric ? (
                <MetricCard
                  metric={activeMetric}
                  isOverlay
                  expandedId={null}
                  setExpandedId={() => {}}
                  onUpdate={() => {}}
                  onDelete={() => {}}
                  dimensions={dimensions}
                  timings={timings}
                  allNames={allNames}
                  userHue={userHue}
                  mode={mode}
                />
              ) : null}
            </DragOverlay>
          </DndContext>

          {metrics.length === 0 && (
            <div className="metric-editor__empty">No metrics defined.</div>
          )}

          <button type="button" onClick={handleAdd} className="metric-editor__add">
            + Metric
          </button>
        </div>
      )}
    </div>
  );
}
